import React, { useContext, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { useToast } from '@givebutter/primitives/toast';
import { pluralize } from '@givebutter/util';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { deleteContacts } from 'api/contacts/contacts';
import { ITableContext, TableContext } from 'components/table/TableContext';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useForceRender } from 'customHooks/useForceRender';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Contact } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface DeleteSelectedContactsProps {
    children?: React.ReactNode;
    onSuccess?: () => void;
}

const DeleteSelectedContacts = ({ onSuccess, children }: DeleteSelectedContactsProps) => {
    const { key, forceRender } = useForceRender();
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const { table } = useContext<ITableContext<Contact>>(TableContext);
    const contactIds = table?.getSelectedRowModel().flatRows.map(item => item.original.id);
    const toast = useToast();

    useEffect(() => {
        if (!contactIds?.length) {
            forceRender();
        }
    }, [contactIds, forceRender]);

    const { mutate: deleteSelectedContacts, isPending } = useMutation({
        mutationFn: () => deleteContacts(accountId, contactIds ?? []),
        onSuccess: data => {
            if (data.number_of_contacts_not_deleted) {
                toast.error(data.message);
            }
            const deletedContacts = data.number_of_contacts_deleted;
            if (deletedContacts) {
                toast.success(
                    `${deletedContacts} ${pluralize(deletedContacts, 'contact')} ${
                        data.number_of_contacts_deleted > 1 ? 'were' : 'was'
                    } deleted successfully`,
                );
            }
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
            forceRender();
            onSuccess?.();
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data.message ?? 'Unknown error occurred');
        },
    });

    return (
        <AlertDialog.Root key={key} type="error">
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon customIcon={faTrash} />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Title>Delete contacts?</AlertDialog.Title>
                    <AlertDialog.Body>
                        {isPending && <Loader />}
                        <AlertDialog.Description>
                            Deleting a contact is an irreversible action and cannot be undone.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <Button
                            className="flex-1"
                            variant="outline"
                            color="neutral"
                            onClick={forceRender}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            className="flex-1"
                            color="error"
                            onClick={() => deleteSelectedContacts()}
                        >
                            Delete
                        </Button>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const DeleteSelectedContactsTrigger = AlertDialog.Trigger;

const Root = DeleteSelectedContacts;
const Trigger = DeleteSelectedContactsTrigger;

export {
    Root,
    Trigger,
    //
    DeleteSelectedContacts,
    DeleteSelectedContactsTrigger,
};
